import React from 'react';
import { Box, Typography, Tooltip, alpha } from '@mui/material';
import {
  Speed as SpeedIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import ServiceStatusBadge from './ServiceStatusBadge';

const getServiceIcon = (name, category) => {
  // Return appropriate icon based on category or name
  const iconMap = {
    'Database': '🗄️',
    'Infrastructure': '🔌',
    'AI Agents': '🤖',
    'Support Services': '⚙️',
    'Workers': '👷',
    'Frontend': '🖥️',
    'Monitoring': '📊',
  };
  return iconMap[category] || '📦';
};

export default function ServiceMiniCard({ service, compact = false, t }) {
  const {
    name,
    description,
    status,
    category,
    current_response_time,
    uptime_percentage,
    error,
  } = service;

  // Translate service name and description
  const translatedName = t ? t(`monitoring.services.${name}.name`, { ns: 'admin', defaultValue: name }) : name;
  const translatedDesc = t ? t(`monitoring.services.${name}.desc`, { ns: 'admin', defaultValue: description }) : description;

  const statusColors = {
    UP: '#10b981',
    DOWN: '#ef4444',
    DEGRADED: '#f59e0b',
    UNKNOWN: '#6b7280',
  };

  const borderColor = statusColors[status] || statusColors.UNKNOWN;

  if (compact) {
    return (
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>{translatedName}</Typography>
            <Typography variant="caption" color="text.secondary">{translatedDesc}</Typography>
            {current_response_time && (
              <Typography variant="caption" display="block">
                Response: {current_response_time.toFixed(0)}ms
              </Typography>
            )}
            {error && (
              <Typography variant="caption" color="error" display="block">
                Error: {error}
              </Typography>
            )}
          </Box>
        }
        arrow
        placement="top"
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: 1.5,
            bgcolor: alpha(borderColor, 0.08),
            border: `1px solid ${alpha(borderColor, 0.3)}`,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: alpha(borderColor, 0.12),
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Box sx={{ fontSize: '1rem' }}>{getServiceIcon(name, category)}</Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 100,
            }}
          >
            {translatedName}
          </Typography>
          <ServiceStatusBadge status={status} size="small" showLabel={false} error={error} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: `1px solid`,
        borderColor: alpha(borderColor, 0.3),
        borderLeft: `4px solid ${borderColor}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: `0 4px 12px ${alpha(borderColor, 0.15)}`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ fontSize: '1.25rem' }}>{getServiceIcon(name, category)}</Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
              {translatedName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {translatedDesc}
            </Typography>
          </Box>
        </Box>
        <ServiceStatusBadge status={status} size="small" error={error} />
      </Box>

      {/* Metrics */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TimerIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {current_response_time ? `${current_response_time.toFixed(0)}ms` : 'N/A'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SpeedIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
          <Typography
            variant="caption"
            sx={{
              color: uptime_percentage >= 99 ? '#10b981' : uptime_percentage >= 95 ? '#f59e0b' : '#ef4444',
              fontWeight: 600,
            }}
          >
            {uptime_percentage.toFixed(1)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
